import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Banknote, Download, Lock, Landmark } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Dialog } from '../../components/nursery/accounts/AccountBits';
import { CashCount } from '../../components/nursery/counter/CashCount';
import { CustomerPicker } from '../../components/nursery/CustomerPicker';
import nurseryService from '../../services/nursery';
import nurseryAccountsService from '../../services/nurseryAccounts';
import nurseryCounterService from '../../services/nurseryCounter';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import { errorMessage } from '../../utils/apiResponse';
import { money, round2 } from '../../utils/nurseryMoney';
import { breakdownTotal, cleanBreakdown, denominationLabel, parseAmount, varianceText } from '../../utils/counterCash';
import { formatDateTimeForInput, formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import type { CashBreakdown, CounterPaymentState, TillMovement, TillMovementKind, TillSession } from '../../types/nurseryCounter';
import type { NurseryCustomer, NurseryOrder } from '../../types/nursery';
import type { InvoiceRow } from '../../types/nurseryAccounts';

/**
 * One till: its float, every cash movement with who made it, what it should
 * hold, and — once closed — what was counted and the difference. Cash in is
 * always received against a customer's invoice, or a walk-in sale's open
 * order (Rob, 2026-10-13); cash out needs a reason. Closing counts the drawer; a till that is out needs
 * a note. The close keeps a float for the next opening and banks the rest, and
 * an over, short or cash out is written to Xero on the cash-on-hand account
 * (2026-10-14). Nothing here can be edited or deleted: a mistake is put right
 * with another movement.
 */

/** Which way a movement moves the till. */
const SIGN: Record<TillMovementKind, 1 | -1> = { sale: 1, refund: -1, pay_in: 1, pay_out: -1 };

const input = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function TillDetail() {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const { id } = useParams<{ id: string }>();
  const timezone = useOrgTimezone();
  const { hasAccess: canWork } = useStockSeat('manage');
  const { hasAccess: canReadInvoices } = useModuleLicense('accounts', ['user', 'admin']);
  const [session, setSession] = useState<TillSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'pay_in' | 'pay_out' | 'close' | 'bank' | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryCounterService.getTill(Number(id));
      if (res.success) setSession(res.data);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  if (!session) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        {error
          ? <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
          : <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />}
      </div>
    );
  }

  const totals = session.totals;
  const open = session.status === 'open';
  const movements = session.movements ?? [];

  const exportCsv = () => {
    const rows = [
      [t('csv.time'), t('csv.what'), t('csv.order'), t('csv.amount'), t('csv.handedOver'), t('csv.change'), t('csv.reason'), t('csv.by')],
      ...movements.map((m) => [
        formatDateTimeInTz(m.created_at, timezone), t(`kind.${m.kind}`), m.order_number ?? '',
        (SIGN[m.kind] * m.amount).toFixed(2), m.tendered?.toFixed(2) ?? '', m.change_given?.toFixed(2) ?? '',
        m.reason ?? '', m.created_by_name ?? '',
      ]),
    ];
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `till-${session.till_name.replace(/\W+/g, '-')}-${session.id}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <>
      <PageHeader
        title={session.till_name}
        subtitle={open ? t('openSince', { when: formatDateTimeInTz(session.opened_at, timezone) }) : t('closedAt', { when: session.closed_at ? formatDateTimeInTz(session.closed_at, timezone) : '' })}
        icon={<Banknote className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv}><Download className="h-4 w-4" /> CSV</Button>
            {open && canWork && (
              <>
                <Button variant="outline" size="sm" onClick={() => setDialog('pay_in')}>{t('kind.pay_in')}</Button>
                <Button variant="outline" size="sm" onClick={() => setDialog('pay_out')}>{t('kind.pay_out')}</Button>
                {(totals?.expected_cash ?? 0) > session.opening_float && (
                  <Button variant="outline" size="sm" onClick={() => setDialog('bank')}><Landmark className="h-4 w-4" /> {t('bank.title')}</Button>
                )}
                <Button size="sm" onClick={() => setDialog('close')}><Lock className="h-4 w-4" /> {t('closeTheTill')}</Button>
              </>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <p className="text-sm"><Link to="/nursery/tills" className="text-primary-700 hover:underline">{t('allTills')}</Link></p>

        {totals && (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {([
              [t('stat.float'), totals.opening_float],
              [t('stat.cashSales', { count: totals.sale_count }), totals.sales],
              [t('stat.refunds'), -totals.refunds],
              [t('kind.pay_in'), totals.pay_ins],
              [t('kind.pay_out'), -totals.pay_outs],
              // Once closed, cash out includes what was banked at the close: the count below says what it held.
              ...(open ? [[t('stat.shouldHold'), totals.expected_cash]] : []),
            ] as [string, number][]).map(([label, value]) => (
              <div key={label} className="bg-white border border-neutral-200 rounded-xl p-3 min-w-0">
                <dt className="text-xs text-neutral-500 break-words">{label}</dt>
                <dd className="text-lg font-semibold text-neutral-900">{money(value)}</dd>
              </div>
            ))}
          </dl>
        )}

        {!open && (
          <section className={`border rounded-xl p-4 space-y-2 ${session.variance != null && Math.abs(session.variance) >= 0.005 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <p className="font-semibold text-neutral-900 break-words">
              {t('countedAgainst', { counted: money(session.counted_cash ?? 0), expected: money(session.expected_cash ?? 0), variance: varianceText(session.variance) })}
            </p>
            <p className="text-sm text-neutral-700 break-words">
              {session.closed_at
                ? t('closedByOn', { name: session.closed_by_name ?? t('someoneGone'), when: formatDateTimeInTz(session.closed_at, timezone) })
                : t('closedBy', { name: session.closed_by_name ?? t('someoneGone') })}
            </p>
            {session.close_notes && <p className="text-sm text-neutral-800 break-words">“{session.close_notes}”</p>}
            {session.closing_breakdown && <BreakdownLine breakdown={session.closing_breakdown} />}
            {session.closing_float != null && (
              <p className="text-sm text-neutral-800 break-words">
                {t('keptAndBanked', { keep: money(session.closing_float), banked: money(session.banked_at_close ?? 0) })}
              </p>
            )}
          </section>
        )}

        {(session.adjustments ?? []).length > 0 && (
          <section className="space-y-2">
            <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('adjust.heading')}</h2>
            <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
              {(session.adjustments ?? []).map((a) => (
                <li key={a.id} className="px-4 py-3 flex items-start gap-3 min-w-0">
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm font-medium text-neutral-900 break-words">{t(`adjust.${a.stage}_${a.kind}`)}</p>
                    <p className="text-xs text-neutral-500">{formatLocalDateInTz(a.posting_date, timezone)}</p>
                    <XeroPostingStatus postingId={a.id} xeroId={a.xero_bank_transaction_id} error={a.xero_push_error}
                      canRetry={canWork} onChanged={load} />
                  </div>
                  <p className={`text-sm font-semibold shrink-0 ${a.kind === 'over' ? 'text-neutral-900' : 'text-red-700'}`}>
                    {a.kind === 'over' ? '+' : '−'}{money(a.amount)}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('everyMovement')}</h2>
          <p className="text-xs text-neutral-500 break-words">
            {t('openedBy', { name: session.opened_by_name ?? t('someoneGone'), amount: money(session.opening_float) })}
          </p>
          {session.opening_breakdown && <BreakdownLine breakdown={session.opening_breakdown} />}
          {session.carried_float != null && (
            <p className="text-xs text-neutral-600 break-words">
              {t('openedAgainst', { carried: money(session.carried_float), variance: varianceText(session.opening_variance ?? 0) })}
              {session.opening_notes ? ` “${session.opening_notes}”` : ''}
            </p>
          )}
          {movements.length === 0 ? (
            <p className="text-sm text-neutral-500">{t('nothingYet')}</p>
          ) : (
            <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
              {movements.map((m) => <MovementRow key={m.id} m={m} timezone={timezone} canReadInvoices={canReadInvoices}
                canRetryBanking={canWork} onChanged={load} />)}
            </ul>
          )}
        </section>
      </div>

      {dialog === 'pay_in' && (
        <CashInDialog sessionId={session.id} onClose={() => setDialog(null)}
          onDone={async () => { setDialog(null); await load(); }} />
      )}
      {dialog === 'pay_out' && (
        <MovementDialog sessionId={session.id} onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
      {dialog === 'close' && totals && (
        <CloseDialog session={session} expected={totals.expected_cash} onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
      {dialog === 'bank' && totals && (
        <BankCashDialog session={session} available={Math.max(0, totals.expected_cash - session.opening_float)}
          onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
    </>
  );
}

function BreakdownLine({ breakdown }: { breakdown: CashBreakdown }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const parts = Object.entries(breakdown)
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([cents, n]) => `${n} × ${denominationLabel(Number(cents))}`);
  return <p className="text-xs text-neutral-600 break-words">{t('countedAs', { parts: parts.join(', ') })}</p>;
}

function MovementRow({ m, timezone, canReadInvoices, canRetryBanking, onChanged }: {
  m: TillMovement;
  timezone: string;
  canReadInvoices: boolean;
  canRetryBanking: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const sign = SIGN[m.kind];
  const retry = async () => {
    if (!m.banking_id) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const res = await nurseryCounterService.retryCashBanking(m.banking_id);
      if (res.success) await onChanged();
      else setRetryError(errorMessage(res, t('bank.retryFailed')));
    } catch (e) {
      setRetryError(errorMessage(e, t('bank.retryFailed')));
    }
    setRetrying(false);
  };
  return (
    <li className="px-4 py-3 flex items-start gap-3 min-w-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-neutral-900 break-words">
          {t(`kind.${m.kind}`)}
          {m.order_id && m.order_number && (
            <> · <Link to={`/nursery/orders/${m.order_id}`} className="text-primary-700 hover:underline">{m.order_number}</Link></>
          )}
          {canReadInvoices && m.invoice_id && m.invoice_number && (
            <> · <Link to={`/accounts/invoices/${m.invoice_id}`} className="text-primary-700 hover:underline">{m.invoice_number}</Link></>
          )}
        </p>
        <p className="text-xs text-neutral-500 break-words">
          {formatDateTimeInTz(m.created_at, timezone)}{m.created_by_name ? ` · ${m.created_by_name}` : ''}
          {m.tendered != null ? t('handedOver', { amount: money(m.tendered) }) : ''}
          {m.change_given ? t('changeGiven', { amount: money(m.change_given) }) : ''}
        </p>
        {m.reason && m.kind !== 'refund' && <p className="text-xs text-neutral-700 break-words">{m.reason}</p>}
        {m.banking_id && (
          <div className="mt-1 text-xs text-neutral-600 space-y-1">
            <p className="break-words">
              {t('bank.deposit', { date: m.deposit_date ? formatLocalDateInTz(m.deposit_date, timezone) : '', reference: m.banking_reference || t('notRecorded') })}
            </p>
            <p className={m.xero_transfer_id ? 'text-green-700' : m.xero_push_error ? 'text-red-700' : 'text-amber-800'}>
              {m.xero_transfer_id ? t('bank.xeroDone', { id: m.xero_transfer_id }) : m.xero_push_error ? t('bank.xeroFailed', { error: m.xero_push_error }) : t('bank.xeroPending')}
            </p>
            {canRetryBanking && !m.xero_transfer_id && m.xero_push_error && (
              <Button variant="outline" size="sm" disabled={retrying} onClick={retry}>
                {retrying ? t('bank.retrying') : t('bank.retry')}
              </Button>
            )}
            {retryError && <p className="text-red-700 break-words" role="alert">{retryError}</p>}
          </div>
        )}
        {m.posting_id && (
          <div className="mt-1">
            <XeroPostingStatus postingId={m.posting_id} xeroId={m.posting_xero_id ?? null} error={m.posting_error ?? null}
              canRetry={canRetryBanking} onChanged={onChanged} />
          </div>
        )}
      </div>
      <p className={`text-sm font-semibold shrink-0 ${sign > 0 ? 'text-neutral-900' : 'text-red-700'}`}>
        {sign > 0 ? '+' : '−'}{money(m.amount)}
      </p>
    </li>
  );
}

/** Where an over, short or cash out stands with Xero, and a retry when it has not gone. */
function XeroPostingStatus({ postingId, xeroId, error, canRetry, onChanged }: {
  postingId: number;
  xeroId: string | null;
  error: string | null;
  canRetry: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const retry = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      const res = await nurseryCounterService.retryCashPosting(postingId);
      if (res.success) await onChanged();
      else setRetryError(errorMessage(res, t('xero.retryFailed')));
    } catch (e) {
      setRetryError(errorMessage(e, t('xero.retryFailed')));
    }
    setRetrying(false);
  };
  return (
    <div className="text-xs space-y-1">
      <p className={`break-words ${xeroId ? 'text-green-700' : error ? 'text-red-700' : 'text-amber-800'}`}>
        {xeroId ? t('xero.done') : error ? t('xero.failed', { error }) : t('xero.pending')}
      </p>
      {canRetry && !xeroId && error && (
        <Button variant="outline" size="sm" disabled={retrying} onClick={retry}>
          {retrying ? t('xero.retrying') : t('xero.retry')}
        </Button>
      )}
      {retryError && <p className="text-red-700 break-words" role="alert">{retryError}</p>}
    </div>
  );
}

function BankCashDialog({ session, available, onClose, onDone }: {
  session: TillSession;
  available: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const timezone = useOrgTimezone();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => formatDateTimeForInput(new Date().toISOString(), timezone).slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = parseAmount(amount);
  const dateValue = new Date(`${date}T12:00:00Z`);
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    && !Number.isNaN(dateValue.getTime())
    && dateValue.toISOString().slice(0, 10) === date;
  const ok = value != null && value > 0 && value <= available && validDate;

  const save = async () => {
    if (!ok || value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.bankCash(session.id, { amount: value, deposit_date: date, reference: reference.trim() || undefined });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('bank.failed')));
    } catch (e) {
      setError(errorMessage(e, t('bank.failed')));
    }
    setBusy(false);
  };

  return (
    <Dialog title={t('bank.title')} subtitle={t('bank.subtitle', { available: money(available), float: money(session.opening_float) })}
      onClose={onClose} footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!ok || busy}>{busy ? t('bank.recording') : t('bank.record')}</Button>
        </>
      }>
      <p className="text-sm text-neutral-700 break-words">{t('bank.explanation')}</p>
      <div>
        <label htmlFor="bank-cash-amount" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.amount')}</label>
        <input id="bank-cash-amount" type="text" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
      </div>
      {value != null && value > available && <p className="text-sm text-red-700">{t('bank.exceeds', { available: money(available) })}</p>}
      <div>
        <label htmlFor="bank-cash-date" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.date')}</label>
        <input id="bank-cash-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} />
      </div>
      <div>
        <label htmlFor="bank-cash-reference" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.reference')}</label>
        <input id="bank-cash-reference" type="text" maxLength={200} value={reference}
          onChange={(e) => setReference(e.target.value)} className={input} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

/** Order statuses the counter takes payment on (HHNodeServer nurseryCounterService PAYABLE_STATUSES). */
const PAYABLE_ORDER_STATUSES = ['draft', 'confirmed', 'partially_allocated', 'allocated', 'picking', 'picked'];

type CashInTarget = { kind: 'invoice'; id: number } | { kind: 'order'; id: number };

/**
 * Cash into the till, always against something the customer owes. Pick the
 * customer (or the walk-in customer), then one of its invoices with a
 * balance, or for the walk-in customer an open order not yet invoiced. An
 * invoice takes part payment and records the change, as Take cash in a till
 * on the invoice page does; an order is paid in full, as Payment at the
 * counter on the order does.
 */
function CashInDialog({ sessionId, onClose, onDone }: {
  sessionId: number;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [customers, setCustomers] = useState<NurseryCustomer[]>([]);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [orders, setOrders] = useState<NurseryOrder[]>([]);
  const [target, setTarget] = useState<CashInTarget | null>(null);
  const [orderState, setOrderState] = useState<CounterPaymentState | null>(null);
  const [cash, setCash] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    nurseryService.getCustomers({ active: true })
      .then((res) => { if (live && res.success) setCustomers(res.data || []); })
      .catch((e: unknown) => { if (live) setError(errorMessage(e, t('cashIn.customersFailed'))); });
    return () => { live = false; };
  }, [t]);

  const customer = customers.find((c) => c.id === customerId) ?? null;
  const isWalkIn = !!customer?.is_walk_in;

  useEffect(() => {
    setInvoices(null);
    setOrders([]);
    setTarget(null);
    if (customerId == null) return;
    let live = true;
    // The customer's own invoices, and any it pays for as a head office. The
    // walk-in customer's sales are also paid before they are invoiced.
    Promise.all([
      nurseryAccountsService.getInvoices({ customer_id: customerId }),
      nurseryAccountsService.getInvoices({ payer_id: customerId }),
      isWalkIn ? nurseryService.getOrders({ customer_id: customerId }) : Promise.resolve(null),
    ]).then(([own, paid, ord]) => {
      if (!live) return;
      const owing = new Map<number, InvoiceRow>();
      for (const inv of [...(own.success ? own.data || [] : []), ...(paid.success ? paid.data || [] : [])]) {
        if (inv.status !== 'draft' && inv.status !== 'cancelled' && Number(inv.balance) > 0.004) owing.set(inv.id, inv);
      }
      const list = [...owing.values()].sort((a, b) => String(a.invoice_date).localeCompare(String(b.invoice_date)));
      const invoicedOrders = new Set(list.map((i) => i.order_id));
      const open = (ord && ord.success ? ord.data || [] : [])
        .filter((o) => o.customer_id === customerId && PAYABLE_ORDER_STATUSES.includes(o.status)
          && o.payment_status !== 'paid' && !invoicedOrders.has(o.id))
        .sort((a, b) => String(a.order_date).localeCompare(String(b.order_date)));
      setInvoices(list);
      setOrders(open);
      if (list.length === 1 && open.length === 0) setTarget({ kind: 'invoice', id: list[0].id });
      else if (list.length === 0 && open.length === 1) setTarget({ kind: 'order', id: open[0].id });
    }).catch((e: unknown) => { if (live) setError(errorMessage(e, t('cashIn.invoicesFailed'))); });
    return () => { live = false; };
  }, [customerId, isWalkIn, t]);

  // An order's amount due is the server's: what it will invoice at, GST included.
  useEffect(() => {
    setOrderState(null);
    if (target?.kind !== 'order') return;
    let live = true;
    nurseryCounterService.getPayment(target.id)
      .then((res) => {
        if (!live) return;
        if (res.success && res.data) setOrderState(res.data);
        else setError(errorMessage(res, t('cashIn.orderFailed')));
      })
      .catch((e: unknown) => { if (live) setError(errorMessage(e, t('cashIn.orderFailed'))); });
    return () => { live = false; };
  }, [target, t]);

  const walkIn = async () => {
    setError(null);
    try {
      const res = await nurseryService.getWalkInCustomer();
      if (res.success && res.data) {
        const c = { ...res.data, is_walk_in: true };
        setCustomers((list) => [c, ...list.filter((x) => x.id !== c.id)]);
        setCustomerId(c.id);
      } else setError(errorMessage(res, t('cashIn.customersFailed')));
    } catch (e) {
      setError(errorMessage(e, t('cashIn.customersFailed')));
    }
  };

  const invoice = target?.kind === 'invoice' ? invoices?.find((i) => i.id === target.id) ?? null : null;
  const orderDue = target?.kind === 'order' && orderState?.can_take ? Number(orderState.amount_due ?? 0) : null;
  const due = invoice ? Number(invoice.balance) : orderDue ?? 0;
  const tendered = parseAmount(cash);
  const applied = tendered == null ? 0 : Math.min(tendered, due);
  const change = tendered == null ? 0 : Math.max(0, round2(tendered - due));
  const short = orderDue != null && tendered != null && tendered + 0.004 < orderDue;
  const ready = invoice != null || (orderDue != null && orderDue > 0);
  const ok = ready && tendered != null && tendered > 0 && !short;

  const save = async () => {
    if (!target || tendered == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = target.kind === 'invoice'
        ? await nurseryCounterService.takeInvoiceCash(target.id, sessionId, tendered)
        : await nurseryCounterService.takeCash(target.id, sessionId, tendered);
      if (res.success) {
        await onDone();
        return;
      }
      setError(errorMessage(res, t('notRecorded')));
    } catch (e) {
      setError(errorMessage(e, t('notRecorded')));
    }
    setBusy(false);
  };

  const choice = (key: string, checked: boolean, pick: () => void, body: ReactNode) => (
    <li key={key}>
      <label className="flex items-start gap-2 rounded-lg border border-neutral-200 px-3 py-2 text-sm cursor-pointer min-w-0">
        <input type="radio" name="cash-in-target" className="mt-0.5" checked={checked} onChange={pick} />
        <span className="min-w-0 flex-1 break-words">{body}</span>
      </label>
    </li>
  );

  return (
    <Dialog
      title={t('move.inTitle')}
      subtitle={t('cashIn.subtitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!ok || busy}>
            {busy ? t('move.recording') : t('cashIn.record', { amount: money(applied) })}
          </Button>
        </>
      }
    >
      <div>
        <label htmlFor="cash-in-customer" className="block text-sm font-medium text-neutral-700 mb-1">{t('cashIn.customer')}</label>
        <CustomerPicker id="cash-in-customer" customers={customers} value={customerId} onChange={setCustomerId} />
        <button type="button" onClick={walkIn} className="mt-1 text-sm text-primary-700 hover:underline">
          {t('cashIn.walkIn')}
        </button>
      </div>
      {customerId != null && (
        <div>
          <p className="block text-sm font-medium text-neutral-700 mb-1">{isWalkIn ? t('cashIn.invoiceOrOrder') : t('cashIn.invoice')}</p>
          {invoices === null ? (
            <p className="text-sm text-neutral-500">{t('cashIn.loadingInvoices')}</p>
          ) : invoices.length === 0 && orders.length === 0 ? (
            <p className="text-sm text-amber-800 break-words">{isWalkIn ? t('cashIn.nothingWalkIn') : t('cashIn.noInvoices')}</p>
          ) : (
            <ul className="space-y-1">
              {invoices.map((inv) => choice(`i${inv.id}`, target?.kind === 'invoice' && target.id === inv.id,
                () => setTarget({ kind: 'invoice', id: inv.id }), (
                  <>
                    <span className="font-medium">{inv.invoice_number}</span>
                    {inv.customer_name && inv.customer_id !== customerId ? ` · ${inv.customer_name}` : ''}
                    {' · '}{t('cashIn.owing', { amount: money(inv.balance) })}
                  </>
                )))}
              {orders.map((o) => choice(`o${o.id}`, target?.kind === 'order' && target.id === o.id,
                () => setTarget({ kind: 'order', id: o.id }), (
                  <>
                    <span className="font-medium">{o.order_number}</span>
                    {' · '}{t('cashIn.notInvoiced')}
                    {target?.kind === 'order' && target.id === o.id && orderState?.can_take && orderState.amount_due != null
                      ? ` · ${t('cashIn.due', { amount: money(orderState.amount_due) })}` : ''}
                  </>
                )))}
            </ul>
          )}
        </div>
      )}
      {target?.kind === 'order' && orderState && !orderState.can_take && (
        <p className="text-sm text-amber-800 break-words">{orderState.blockers.join(' ')}</p>
      )}
      {ready && (
        <div>
          <label htmlFor="cash-in-tendered" className="block text-sm font-medium text-neutral-700 mb-1">{t('cashIn.received')}</label>
          <input id="cash-in-tendered" type="text" inputMode="decimal" value={cash}
            onChange={(e) => setCash(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
          {orderDue != null && <p className="mt-1 text-xs text-neutral-600">{t('cashIn.orderInFull', { amount: money(orderDue) })}</p>}
        </div>
      )}
      {ready && tendered != null && tendered > 0 && !short && (
        <dl className="rounded-lg bg-neutral-50 p-3 text-sm space-y-1">
          <div className="flex justify-between gap-3">
            <dt>{invoice ? t('cashIn.applied') : t('cashIn.appliedOrder')}</dt><dd className="font-medium">{money(applied)}</dd>
          </div>
          {change > 0 && <div className="flex justify-between gap-3"><dt>{t('cashIn.change')}</dt><dd className="font-medium">{money(change)}</dd></div>}
        </dl>
      )}
      {short && <p className="text-sm text-amber-800 break-words">{t('cashIn.short', { amount: money(orderDue ?? 0) })}</p>}
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

function MovementDialog({ sessionId, onClose, onDone }: {
  sessionId: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const kind = 'pay_out';
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = parseAmount(amount);
  const ok = value != null && value > 0 && reason.trim().length > 0;

  const save = async () => {
    if (value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.addMovement(sessionId, { kind, amount: value, reason: reason.trim() });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('notRecorded')));
    } catch (e) {
      setError(errorMessage(e, t('notRecorded')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={t('move.outTitle')}
      subtitle={t('move.outSubtitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!ok || busy}>{busy ? t('move.recording') : t('move.record')}</Button>
        </>
      }
    >
      <div>
        <label htmlFor="move-amount" className="block text-sm font-medium text-neutral-700 mb-1">{t('move.amount')}</label>
        <input id="move-amount" type="text" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
      </div>
      <div>
        <label htmlFor="move-reason" className="block text-sm font-medium text-neutral-700 mb-1">{t('move.reason')}</label>
        <input id="move-reason" type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={input}
          placeholder={t('move.outPlaceholder')} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

function CloseDialog({ session, expected, onClose, onDone }: {
  session: TillSession;
  expected: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const timezone = useOrgTimezone();
  const [count, setCount] = useState<CashBreakdown>({});
  const [typed, setTyped] = useState('');
  const [notes, setNotes] = useState('');
  const [keepTyped, setKeepTyped] = useState('');
  const [date, setDate] = useState(() => formatDateTimeForInput(new Date().toISOString(), timezone).slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const counted = cleanBreakdown(count);
  const value = counted ? breakdownTotal(counted) : parseAmount(typed);
  const variance = value != null ? round2(value - expected) : null;
  const out = variance != null && Math.abs(variance) >= 0.005;
  // The next float: this till's float unless changed, never more than was counted (the server's rule).
  const keep = parseAmount(keepTyped) ?? (value != null ? Math.min(session.opening_float, value) : session.opening_float);
  const keepTooMuch = value != null && keep > value + 0.004;
  const bank = value != null && !keepTooMuch ? round2(value - keep) : 0;
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const ok = value != null && (!out || notes.trim().length > 0) && !keepTooMuch && (bank <= 0 || validDate);

  const close = async () => {
    if (value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.closeTill(session.id, {
        counted_cash: value, breakdown: counted, notes: notes.trim() || undefined,
        keep_float: keep, deposit_date: bank > 0 ? date : undefined, reference: reference.trim() || undefined,
      });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('close.failed')));
    } catch (e) {
      setError(errorMessage(e, t('close.failed')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      wide
      title={t('close.title', { name: session.till_name })}
      subtitle={t('close.subtitle', { amount: money(expected) })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={close} disabled={!ok || busy}>{busy ? t('close.closing') : t('closeTheTill')}</Button>
        </>
      }
    >
      <CashCount idPrefix="close" value={count} onChange={setCount} denominations={session.denominations} />
      {!counted && (
        <div>
          <label htmlFor="close-typed" className="block text-sm font-medium text-neutral-700 mb-1">{t('close.typed')}</label>
          <input id="close-typed" type="text" inputMode="decimal" value={typed}
            onChange={(e) => setTyped(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
        </div>
      )}
      {value != null && (
        <p className={`text-base font-semibold ${out ? 'text-red-700' : 'text-green-700'}`} role="status">
          {t('close.counted', { amount: money(value), variance: varianceText(variance) })}
        </p>
      )}
      <div>
        <label htmlFor="close-notes" className="block text-sm font-medium text-neutral-700 mb-1">
          {out ? t('close.noteNeeded') : t('close.note')}
        </label>
        <textarea id="close-notes" rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} className={input} />
        {out && <p className="mt-1 text-xs text-neutral-600 break-words">{t('close.varianceToXero')}</p>}
      </div>
      {value != null && (
        <div className="space-y-3 rounded-lg bg-neutral-50 p-3">
          <div>
            <label htmlFor="close-keep" className="block text-sm font-medium text-neutral-700 mb-1">{t('close.keep')}</label>
            <input id="close-keep" type="text" inputMode="decimal" value={keepTyped}
              onChange={(e) => setKeepTyped(e.target.value.replace(/[^\d.]/g, ''))} className={input}
              placeholder={keep.toFixed(2)} />
            {keepTooMuch
              ? <p className="mt-1 text-sm text-red-700 break-words">{t('close.keepTooMuch', { amount: money(value) })}</p>
              : <p className="mt-1 text-xs text-neutral-600 break-words">{t('close.keepHint')}</p>}
          </div>
          {!keepTooMuch && (
            <p className="text-sm font-medium text-neutral-900 break-words" role="status">
              {bank > 0
                ? t('close.bankLine', { bank: money(bank), keep: money(keep) })
                : t('close.nothingBanked', { keep: money(keep) })}
            </p>
          )}
          {bank > 0 && !keepTooMuch && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="min-w-0">
                <label htmlFor="close-deposit-date" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.date')}</label>
                <input id="close-deposit-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} />
              </div>
              <div className="min-w-0">
                <label htmlFor="close-reference" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.reference')}</label>
                <input id="close-reference" type="text" maxLength={200} value={reference}
                  onChange={(e) => setReference(e.target.value)} className={input} />
              </div>
            </div>
          )}
        </div>
      )}
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

export default TillDetail;
